import './styles.scss';

import { Datetime, getClasses, getProperty, queryInput, saveFile } from '@/utils';
import { IMPORT_TYPES, MONTH_END, MONTH_START, PENDING_STATUS } from '@/constants';
import { Import, ImportStatusEnum, SortDirectionEnum } from '@/models/gen/graphql';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { AirportGroupDropdown } from '@/components/AirportDropdown';
import { ConnectionDetails } from '@/utils/custom';
import DateRangePicker from '@/components/DateRangePicker';
import EnumDropdown from '@/components/EnumDropdown';
import FormButton from '@/components/FormButton';
import ImportHistoryTableHeader from './ImportHistoryTableHeader';
import ImportHistoryTableRow from './ImportHistoryTableRow';
import ImportProviderDropdown from '@/components/ImportProviderDropdown';
import { LoadingBlur } from '@/components/LoadingSpinner';
import PageInfo from '@/components/PageInfo';
import QueryFilters from '@/components/QueryFilters';
import { SEARCH_IMPORTS_PAGE_SIZE } from '@/api/services/imports/searchImports';
import Table from '@/common/Table';
import UserDropdown from '@/components/UserDropdown';
import { createPersistentComponentState } from '@/state';
import { customSearchStorage } from '@/state/storage';
import equal from 'fast-deep-equal/es6/react';
import runDownloadImports from '@/api/services/imports/runDownloadImports';
import runRollbackImports from '@/api/services/imports/runRollbackImports';
import { titleCase } from '@/utils/strings';
import useImportHistoryTableSorting from './useImportHistoryTableSorting';
import usePopState from '@/hooks/usePopState';
import { useSearchImportHistoryTable } from '@/api/services/imports/searchImportHistoryTable';
import { useSearchParams } from 'react-router-dom';

type ImportHistoryFiltersState = {
  airportCode: string[];
  providerId: string;
  from: string;
  to: string;
  ingestedBy: string;
  status: ImportStatusEnum | '';
  type: string;
};

const initImportHistoryFilters: ImportHistoryFiltersState = {
  airportCode: [],
  providerId: '',
  from: MONTH_START,
  to: MONTH_END,
  ingestedBy: '',
  status: '',
  type: 'MANIFEST',
};

const useImportHistoryFilters = createPersistentComponentState<ImportHistoryFiltersState>(
  'useImportHistoryFilters',
  initImportHistoryFilters,
  {
    live: false,
    storage: customSearchStorage,
    reviver: (key: string, value: any) => {
      switch (key) {
        case 'type':
          const type = (Array.isArray(value) ? value : [value]).filter((key) => IMPORT_TYPES.includes(key));
          if (type.length === 0) return null;
          return type;
        case 'status':
          return Object.values(ImportStatusEnum).includes(value) ? value : initImportHistoryFilters.status;
        default: {
          return value;
        }
      }
    },
  }
);

const ImportHistoryTable = (): ReactElement => {
  const sortColumn = useImportHistoryTableSorting(({ state }) => state.column);
  const sortDirection = useImportHistoryTableSorting(({ state }) => state.direction);
  const resetSort = useImportHistoryTableSorting(({ state }) => state.resetSort);

  const filters = useImportHistoryFilters(({ state }) => state);
  const setFilters = useImportHistoryFilters(({ setState }) => setState);
  const saveState = useImportHistoryFilters(({ saveState }) => saveState);
  const rehydrateState = useImportHistoryFilters(({ rehydrate }) => rehydrate);

  const [lastFilters, setLastFilters] = useState<ImportHistoryFiltersState>(filters);

  const [{ data, loading }, { refetch, fetchMore }] = useSearchImportHistoryTable();
  const { rows = [], hasNextPage = false, totalCount = 0 } = data || {};

  const [queryString] = useSearchParams();

  const sortedRows = useMemo((): Import[] => {
    return [...rows].sort((a: Import, b: Import): number => {
      if (!sortColumn || !sortDirection) return 0;
      const aValue = (getProperty(sortColumn, a) as string) ?? '';
      const bValue = (getProperty(sortColumn, b) as string) ?? '';
      const aLower = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
      const bLower = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;
      if (aLower < bLower) return sortDirection === SortDirectionEnum.Asc ? -1 : 1;
      if (aLower > bLower) return sortDirection === SortDirectionEnum.Asc ? 1 : -1;
      return 0;
    });
  }, [rows, sortColumn, sortDirection]);

  const downloadManifest = async (id: string, name?: string): Promise<void> => {
    const downloadResult = await runDownloadImports(id);
    const downloadURL = downloadResult.length === 1 ? downloadResult[0].url : undefined;
    if (downloadURL) saveFile(downloadURL, name);
  };

  const handleRollback = async (id: string): Promise<void> => {
    await runRollbackImports([id]);
    refetch();
  };

  const convertFiltersToQuery = useCallback(
    (filters: ImportHistoryFiltersState) => {
      const { from = MONTH_START, to = MONTH_END, ingestedBy, providerId, status, type, airportCode } = filters;
      const defaultStatus = [ImportStatusEnum.Ingested, ImportStatusEnum.RolledBack, ImportStatusEnum.RollingBack];
      const query = {
        id: queryInput(queryString.has('filter') ? [queryString.get('filter')] : null),
        ingestedBy: queryInput(ingestedBy || null),
        providerId: queryInput(providerId || null),
        status: queryInput(status || defaultStatus),
        type: queryInput(type),
        airportCode: airportCode?.length ? queryInput(airportCode) : null,
        ingestStarted: queryInput.date([from || MONTH_START, to || MONTH_END], true, SortDirectionEnum.Desc, 0),
      };
      return query;
    },
    [queryString]
  );

  const runSearchImports = useCallback(
    async (filters: ImportHistoryFiltersState): Promise<void> => {
      const query = convertFiltersToQuery(filters);
      await refetch([query]);
    },
    [convertFiltersToQuery]
  );

  const getMore = async (after: number): Promise<ConnectionDetails<Import>> => {
    if (loading || !hasNextPage) return;
    const result = await fetchMore([convertFiltersToQuery(lastFilters)], {
      page: Math.round(after / SEARCH_IMPORTS_PAGE_SIZE),
      merge: true,
    });
    return result;
  };

  const handleChange = useCallback(
    (name: string) =>
      (value: unknown): void =>
        setFilters((current) => ({ ...current, [name]: value })),
    [setFilters]
  );

  const handleSubmit = useCallback((): void => {
    // get the latest state from closure to ensure it's always accurate
    const { state } = useImportHistoryFilters.getState();
    saveState();
    runSearchImports(state);
    setLastFilters(state);
  }, [filters, saveState, runSearchImports]);

  const handleReset = useCallback((): void => {
    const from = new Datetime().startOf('day').toString();
    const to = new Datetime().endOf('day').toString();
    setFilters({ ...initImportHistoryFilters, from, to });
    setLastFilters({ ...initImportHistoryFilters, from, to });
    resetSort();
    saveState();
  }, [setFilters, saveState, resetSort]);

  const handlePopState = useCallback(() => rehydrateState().then(handleSubmit), [rehydrateState, handleSubmit]);
  usePopState(handlePopState);

  useEffect((): void => {
    handlePopState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortColumn, sortDirection]);

  return (
    <>
      <PageInfo>
        {sortedRows.length || 0} / {totalCount || 0} Imports
      </PageInfo>
      <QueryFilters className="ImportHistoryTableFilters justify-content-between">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateRangePicker
              name="dates"
              isDirty={filters.from !== lastFilters.from || filters.to !== lastFilters.to}
              value={[filters.from, filters.to]}
              onChange={(event) => {
                const [from, to] = event.target.value?.split?.(' - ') || [null, null];
                handleChange('from')(from);
                handleChange('to')(to);
              }}
            />
          </QueryFilters.Control>
          <QueryFilters.Input>
            <UserDropdown
              name="ingestedBy"
              isDirty={filters.ingestedBy !== lastFilters.ingestedBy}
              onChange={handleChange('ingestedBy')}
              value={filters.ingestedBy}
              options={{ locale: { 'Select...': 'Imported By' } }}
              airports={filters.airportCode || []}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <AirportGroupDropdown
              name="airportCode"
              isDirty={equal(filters.airportCode, lastFilters.airportCode)}
              value={filters.airportCode}
              onChange={handleChange('airportCode')}
              onEnter={handleSubmit}
              options={{ locale: { 'Select...': 'Airport' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <ImportProviderDropdown
              name="providerId"
              isDirty={filters.providerId !== lastFilters.providerId}
              onChange={handleChange('providerId')}
              value={filters.providerId}
              options={{ locale: { 'Select...': 'Airline' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <EnumDropdown
              name="status"
              onChange={handleChange('status')}
              isDirty={equal(filters.status, lastFilters.status)}
              value={filters.status}
              enum={Object.values(PENDING_STATUS).reduce(
                (acc, value) => {
                  acc[titleCase(value.replace(/_/g, ' '))] = value;
                  return acc;
                },
                {} as Record<string, string>
              )}
              options={{ locale: { 'Select...': 'Status' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <EnumDropdown
              name="type"
              isDirty={equal(filters.type, lastFilters.type)}
              onChange={handleChange('type')}
              value={filters.type}
              enum={Object.values(IMPORT_TYPES).reduce(
                (acc, value) => {
                  acc[titleCase(value.replace(/_/g, ' '))] = value;
                  return acc;
                },
                {} as Record<string, string>
              )}
              options={{ locale: { 'Select...': 'Type' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <FormButton name="submitFilters" variant="success" className="border-white" disabled={loading} onClick={handleSubmit}>
              Go
            </FormButton>
            <FormButton name="resetFilters" variant="outline-secondary-subtle" onClick={handleReset}>
              Reset
            </FormButton>
          </QueryFilters.Control>
        </div>
      </QueryFilters>
      <LoadingBlur loading={loading} />
      <Table
        className={getClasses('ImportHistoryTable')}
        header={<ImportHistoryTableHeader />}
        onEnd={rows.length < totalCount ? () => getMore(rows.length) : undefined}
        showControls
      >
        {sortedRows.map(
          (row: Import): ReactElement => (
            <ImportHistoryTableRow key={row.id} row={row} onDownload={downloadManifest} onRollback={handleRollback} />
          )
        )}
        {!sortedRows.length && (
          <Table.Row style={{ gridTemplateColumns: '1fr' }}>
            <Table.Cell className="text-center">
              <i className="fa fa-inbox" /> <span>No Records</span>
            </Table.Cell>
          </Table.Row>
        )}
      </Table>
    </>
  );
};

export default ImportHistoryTable;
